import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { registerUser } from "../utils/api";
import { useAuth } from "../context/AuthContext";

export default function Register() {
  const [searchParams] = useSearchParams();
  const initialRole = searchParams.get("role") === "barber" ? "barber" : "customer";

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false); // ✅ eye toggle
  const [role, setRole] = useState(initialRole);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    if (!/^[6-9]\d{9}$/.test(phone)) {
    setError("Please enter a valid Indian mobile number.");
    setLoading(false);
    return;
  }
    try {
      const res = await registerUser({ name, email, phone, password, role });
      const actualRole = res.data.user?.role || role;
      login(res.data.token, res.data.user);
      if (actualRole === "barber") {
        navigate("/create-shop", { replace: true });
      } else {
        navigate("/shops", { replace: true });
      }
    } catch (err) {
      setError(err.response?.data?.message || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>

        <div style={styles.logoWrap}>
          <span style={styles.logoIcon}>💈</span>
          <span style={styles.logoText}>TrimSpot</span>
        </div>
        <h2 style={styles.title}>Create Free Account</h2>
        <p style={styles.sub}>Join thousands of users on TrimSpot</p>

        {error && <div style={styles.error}>{error}</div>}

        {/* Role Selector */}
        <div style={styles.roleRow}>
          {["customer", "barber"].map((r) => (
            <div key={r} onClick={() => setRole(r)}
              style={{ ...styles.roleBtn, ...(role === r ? styles.roleBtnActive : {}) }}>
              {r === "customer" ? "👤 Customer" : "💼 Salon / Spa"}
            </div>
          ))}
        </div>

        <div style={styles.roleDesc}>
          {role === "customer"
            ? "📅 Book salon appointments instantly"
            : "🏪 List your salon & get more customers — 45 days FREE!"}
        </div>

        <form onSubmit={handleSubmit} autoComplete="off">

          <label style={styles.label}>Full Name</label>
          <input
            type="text"
            placeholder="e.g. Rahul Kumar"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={styles.input}
            autoComplete="off"
            required
          />

          <label style={styles.label}>Email Address</label>
          <input
            type="text"
            placeholder="e.g. rahul@gmail.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={styles.input}
            autoComplete="off"
            required
          />

          <label style={styles.label}>Phone Number</label>
          <input
            type="text"
            inputMode="numeric"
            placeholder="e.g. 9876543210"
            value={phone}
            onChange={(e) => {
              const val = e.target.value.replace(/[^0-9]/g, "");
              setPhone(val);
            }}
            style={styles.input}
            autoComplete="off"
            maxLength={10}
            required
          />

          <label style={styles.label}>Password</label>
          {/* ✅ Password field with eye toggle */}
          <div style={styles.passwordWrap}>
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Minimum 6 characters"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.passwordInput}
              autoComplete="new-password"
              required
              minLength={6}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={styles.eyeBtn}
            >
              {showPassword ? "🙈" : "👁️"}
            </button>
          </div>

          <button type="submit" style={styles.btn} disabled={loading}>
            {loading ? "Creating account..." : "Create Free Account →"}
          </button>
        </form>

        {role === "barber" && (
          <div style={styles.freeTrial}>
            🎉 First 45 days completely FREE! No credit card needed.
          </div>
        )}

        <p style={styles.footer}>
          Already have an account?{" "}
          <Link to="/login" style={styles.link}>Login here →</Link>
        </p>

      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg-primary)", padding: "20px" },
  card: { background: "var(--bg-card)", padding: "36px 32px", borderRadius: "20px", width: "100%", maxWidth: "440px", border: "1px solid var(--border)", boxShadow: "0 20px 60px rgba(0,0,0,0.5)" },
  logoWrap: { display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", marginBottom: "16px" },
  logoIcon: { fontSize: "32px" },
  logoText: { fontSize: "1.5rem", fontWeight: "900", color: "white" },
  title: { textAlign: "center", fontSize: "1.6rem", color: "white", marginBottom: "6px" },
  sub: { textAlign: "center", color: "var(--text-muted)", marginBottom: "24px", fontSize: "14px" },
  error: { background: "rgba(233,69,96,0.1)", color: "#ff6b6b", padding: "12px", borderRadius: "8px", marginBottom: "16px", fontSize: "14px", border: "1px solid rgba(233,69,96,0.3)" },
  roleRow: { display: "flex", gap: "10px", marginBottom: "10px" },
  roleBtn: { flex: 1, padding: "12px", border: "1px solid var(--border)", borderRadius: "10px", textAlign: "center", cursor: "pointer", fontSize: "13px", color: "var(--text-muted)", background: "var(--bg-secondary)" },
  roleBtnActive: { border: "1px solid var(--accent)", background: "rgba(233,69,96,0.1)", color: "var(--accent)", fontWeight: "bold" },
  roleDesc: { background: "var(--bg-secondary)", padding: "10px 14px", borderRadius: "8px", fontSize: "12px", color: "var(--text-muted)", marginBottom: "20px", textAlign: "center", border: "1px solid var(--border)" },
  label: { display: "block", color: "var(--text-secondary)", fontSize: "13px", fontWeight: "500", marginBottom: "6px" },
  input: { width: "100%", padding: "13px 14px", borderRadius: "10px", marginBottom: "16px", fontSize: "15px", boxSizing: "border-box" },
  // ✅ Password wrap with eye button
  passwordWrap: { position: "relative", marginBottom: "16px" },
  passwordInput: { width: "100%", padding: "13px 48px 13px 14px", borderRadius: "10px", fontSize: "15px", boxSizing: "border-box", background: "var(--bg-card)", color: "var(--text-primary)", border: "1px solid var(--border)" },
  eyeBtn: { position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", fontSize: "18px", padding: "4px", lineHeight: 1 },
  btn: { width: "100%", padding: "14px", background: "var(--accent)", color: "white", border: "none", borderRadius: "10px", fontSize: "16px", cursor: "pointer", fontWeight: "bold", marginBottom: "12px" },
  freeTrial: { background: "rgba(76,175,80,0.1)", color: "#4caf50", padding: "10px 14px", borderRadius: "8px", fontSize: "13px", textAlign: "center", marginBottom: "16px", border: "1px solid rgba(76,175,80,0.3)" },
  footer: { textAlign: "center", marginTop: "8px", color: "var(--text-muted)", fontSize: "14px" },
  link: { color: "var(--accent)", textDecoration: "none", fontWeight: "bold" },
};